import { ROLES } from '../constants/roles';
import { getManageableHouseIds, type HouseViewer } from './houseAccess';

export interface RunnerProfile extends HouseViewer {
  listers?: string[];
}

export interface ListerProfile extends HouseViewer {
  runnerId?: string | null;
}

/** True when a staff profile is linked to a house (primary or via houseIds). */
export function staffBelongsToHouse(
  staff: HouseViewer | null | undefined,
  houseId: string,
): boolean {
  if (!staff || !houseId) return false;
  if (staff.houseId === houseId) return true;
  return getManageableHouseIds(staff).includes(houseId);
}

/** Primary house for a staff member (defaults to sole houseId). */
export function primaryHouseId(staff: HouseViewer | null | undefined): string {
  const ids = getManageableHouseIds(staff);
  if (staff?.houseId && ids.includes(staff.houseId)) return staff.houseId;
  return ids[0] ?? staff?.houseId ?? '';
}

export function isMultiHouseStaff(staff: HouseViewer | null | undefined): boolean {
  return getManageableHouseIds(staff).length > 1;
}

/** Runner attends N listers; ids come from runner.listers. */
export function getRunnerListerIds(runner: RunnerProfile | null | undefined): string[] {
  if (!runner || !Array.isArray(runner.listers)) return [];
  return runner.listers.filter(Boolean);
}

/** Listers assigned to a runner, optionally scoped to the active house. */
export function filterListersForRunner(
  runner: RunnerProfile,
  listers: ListerProfile[],
  activeHouseId?: string,
): ListerProfile[] {
  const assigned = new Set(getRunnerListerIds(runner));
  return listers.filter((l) => {
    if (l.role !== ROLES.LISTER || !l.id || !assigned.has(l.id)) return false;
    if (!activeHouseId) return true;
    return staffBelongsToHouse(l, activeHouseId);
  });
}

/**
 * Default: runner serves listers from the same primary house.
 * Multi-house staff may share another assigned house when allowCrossHouse is true.
 */
export function canRunnerServeLister(
  runner: RunnerProfile,
  lister: ListerProfile,
  { allowCrossHouse = false } = {},
): boolean {
  if (!lister.id || !getRunnerListerIds(runner).includes(lister.id)) return false;

  const runnerHouses = getManageableHouseIds(runner);
  const listerHouses = getManageableHouseIds(lister);
  const sharesHouse = runnerHouses.some((h) => listerHouses.includes(h));
  if (!sharesHouse) return false;

  if (allowCrossHouse) return true;
  return primaryHouseId(runner) === primaryHouseId(lister);
}
